import { CreatePointRequest, IntegrationException, LatLng, UserTag, circle } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { isDefined, sortLatLngByDistance } from '../helpers/utils';

import { CategorySelector } from './theme/category-selector';
import { Dialog } from '@material/mwc-dialog';
import { FormInput } from './theme/form-input';
import { FormTextarea } from './theme/form-textarea';
import { TextField } from '@material/mwc-textfield';
import accountInfo from '../helpers/state/account-info';
import featuresInfo from '../helpers/state/features-info';
import { find_nearby_places } from '../helpers/api/points-api';
import { ZERO_OBJECT_ID, googleTypesSearchArray } from '../helpers/constants';
import { ifDefined } from 'lit/directives/if-defined';
import { logWithAmplitude } from '../helpers/api/api';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('nearby-search-component')
export class NearbySearchComponent extends LitElement {
    static amplitude_callsite = 'Nearby Search Component';

    @query('#containWords')
    containWordsElement!: FormInput;

    @query('#editCreatePoint')
    editCreatePointModal!: Dialog;

    @query('#pointDescription')
    pointDescriptionElement!: FormTextarea;

    @query('#pointName')
    pointNameElement!: FormInput;

    @query('#categorySelector')
    private readonly categorySelectorElement?: CategorySelector;

    @query('#search')
    searchElement!: TextField;

    @property({type: Array})
    searchTermsFlat: Array<[string, string]> = [];

    @state()
    keywordsList: string[] = [];

    @state()
    topicTags: UserTag[] = [];

    @state()
    searchTypes?: Array<[string, string]> = [];

    @state()
    searchKeyword?: string;

    @state()
    autoCompleteResults: Array<[string, string]> = [];

    @state()
    selectedSearchResults: CreatePointRequest[] = [];

    @state()
    allSearchResults: CreatePointRequest[] = [];

    @state()
    loadingToMap: boolean = false;

    @state()
    loadingNearbyPlaces: boolean = false;

    @state()
    focusedMarker?: CreatePointRequest;

    @state()
    pointInEdit?: CreatePointRequest;

    @state()
    searchCircle?: google.maps.Circle;

    @state()
    noResult: boolean = false;

    // listen for changes to this.clearSearch
    // if true, clear all filters
    async firstUpdated() {
        this.getAllSearchTerms();
        logWithAmplitude(NearbySearchComponent.amplitude_callsite, 'Opened Nearby Search');
    }

    private resetFilters() {
        this.keywordsList = [];
        this.allSearchResults = [];
        this.selectedSearchResults = [];
        this.noResult = false;
        this.requestUpdate();
        this.dispatchEvent(new CustomEvent(
            'reset-nearby-search',
        ));
    }

    private getAllSearchTerms() {
        // create flat array of all search terms
        // get search_terms from constants googleTypesSearchArray;
        const searchTerms = googleTypesSearchArray;
        const searchTermsFlat: Array<[string, string]> = [];
        for (const term of searchTerms) {
            const terms: string[] = term.search_terms;
            for (const subterm of terms) {
                searchTermsFlat.push([subterm, term.google_place_id]);
            }
        }
        this.searchTermsFlat = searchTermsFlat;

        // set autocomplete by getting the first 5 terms, skip terms that have the same second value that have already been added
        for (const point of pointCache.points) {
            // grab the first google tag, if it exists
            const googleTag = point.tag_info?.google_tags?.[0];
            // if the google tag is in the search terms, add it to the autocomplete results
            if (googleTag) {
                const found = this.searchTermsFlat.find((term) => term[1] === googleTag);
                if (found && !this.autoCompleteResults.includes(found)) {
                    this.autoCompleteResults.push(found);
                }
            }

            if (this.autoCompleteResults.length >= 10) {
                break;
            }
        }

        if (this.autoCompleteResults.length < 10) {
            // if there aren't enough google tags, add the first 5 search terms
            for (const term of this.searchTermsFlat) {
                if (!this.autoCompleteResults.includes(term)) {
                    this.autoCompleteResults.push(term);
                }
                if (this.autoCompleteResults.length >= 10) {
                    break;
                }
            }
        }
    }

    private searchForTypes(search: string) {
        // suggest up to 5 types that match the inputted search
        // get search_terms from constants googleTypesSearchArray;
        // check if any of the tearms in the this.searchTermsFlat array include the search
        // if so return first 5 in keywords list
        search = search.toLowerCase();
        const newResults: Array<[string, string]> = [];
        for (const term of this.searchTermsFlat) {
            const lowercase_term = term[0].toLowerCase();
            if (lowercase_term.includes(search)) {
                // if its an exact match, add it to the beginning of the list. this is for things like "restaurant"
                if (lowercase_term === search) {
                    newResults.unshift(term);
                }
                else {
                    newResults.push(term);
                }
            }
        }
        this.autoCompleteResults = [...this.searchTypes ?? [], ...newResults];
        // if the results have the same second value, only show the first one
        const uniqueTypes: Array<[string, string]> = [];
        for (const type of this.autoCompleteResults) {
            if (!uniqueTypes.some((t) => t[1] === type[1])) {
                uniqueTypes.push(type);
            }
        }
        this.autoCompleteResults = uniqueTypes.slice(0, 7 + (this.searchTypes?.length ?? 0));
        this.requestUpdate();
    }

    private addKeyword(keyword: string) {
        this.searchKeyword = keyword;
        this.requestUpdate();
    }

    private removeKeyword(keyword: string) {
        this.searchKeyword = undefined;
        this.requestUpdate();
    }

    private async addToMap() {
        this.loadingToMap = true;
        const points = this.selectedSearchResults;
        await pointCache.fetchAndAddMany(points);
        this.loadingToMap = false;
        this.close();
    }

    private updateType(type: [string, string]) {
        // check if the type is already in the list
        const currentSearchTypes = this.searchTypes?.map((t) => t[1]) ?? [];
        if ((currentSearchTypes.includes(type[1]))) {
            // if so, remove it
            this.searchTypes = this.searchTypes?.filter((t) => t[1] !== type[1]);
        }
        else {
            // check if a keyword is set. If so, remove other types and add this one
            // if a keyword is not set, add this type (with keyword search you can only search one type at a time)
            if (isDefined(this.searchKeyword)) {
                this.searchTypes = [type];
            }
            else {
                this.searchTypes = [...this.searchTypes ?? [], type];
            }
        }
        this.requestUpdate();
    }

    private async getNearbyPlaces() {
        this.loadingNearbyPlaces = true;
        let places: CreatePointRequest[] = [];
        const center = this.searchCircle?.getCenter();
        const latLng: LatLng = {
            lat: center ? center.lat() : 0,
            lng: center ? center.lng() : 0
        };

        // round radius to 0 decimal places
        const radius = Math.round(this.searchCircle?.getRadius() ?? 0);
        const types = this.searchTypes?.map((t) => t[1]);

        let num_places = 20;
        if (pointCache.pointsRemaining < 20) {
            num_places = pointCache.pointsRemaining;
        }

        const search_circle: circle =  {
            radius: this.searchCircle?.getRadius() ?? 0,
            center: this.searchCircle?.getCenter()?.toJSON() ?? topicData.topic?.default_map_center?.latLng ?? {lat: 0, lng: 0}
        };


        if (isDefined(latLng) && isDefined(radius)) {
            places = await find_nearby_places(NearbySearchComponent.amplitude_callsite, num_places, search_circle, undefined, this.searchKeyword, types, true);
        };

        // sort places by distance from center
        const sortedLatLongs = sortLatLngByDistance(
            places.map((p) => p.location.latLng).filter((latLng): latLng is LatLng => latLng !== undefined),
            latLng
        );

        places = sortedLatLongs
            .map((latLng) => places.find((p) => p.location.latLng === latLng))
            .filter(Boolean) as CreatePointRequest[];

        // sometimes google returns places that are outside of the circle, deselect those
        this.selectedSearchResults = places;
        const isInCirclePromises = this.selectedSearchResults.map(async (place) =>
            place.location.latLng ? await this.isInCircle(place.location.latLng) : await Promise.resolve(false)
        );
        const isInCircleResults = await Promise.all(isInCirclePromises);
        this.selectedSearchResults = this.selectedSearchResults.filter(
            (_, index) => isInCircleResults[index]
        );

        // If this is welcome home relocations, set the category to "my places"
        if (topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations) {
            const category_id = topicData.categories.find((c) => c.name.toLowerCase().includes('my places'))?._id?.$oid ?? topicData.categories[2]?._id?.$oid ?? topicData.categories[0]?._id?.$oid ?? ZERO_OBJECT_ID;
            this.selectedSearchResults.forEach((p) => {
                p.topic_category_id.$oid = category_id;
            });
        }

        this.allSearchResults = places;
        this.newSearchResults(this.selectedSearchResults);
        this.loadingNearbyPlaces = false;
        if (this.allSearchResults.length === 0) {
            this.noResult = true;
        }

        this.requestUpdate();
    }

    private async isInCircle(latLng: LatLng): Promise<boolean> {
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(latLng.lat, latLng.lng),
            this.searchCircle?.getCenter() ?? new google.maps.LatLng(0, 0)
        );
        const radius = this.searchCircle?.getRadius() ?? 0;
        return distance <= radius;
    };

    private newSearchResults(results: CreatePointRequest[]) {
        this.dispatchEvent(
            new CustomEvent('on-new-search-result', {
                detail: {
                    results
                }
            })
        );
    }

    private updateSearchResults(added: CreatePointRequest[] = [], removed: CreatePointRequest[] = []) {
        this.dispatchEvent(
            new CustomEvent('on-updated-search-result', {
                detail: {
                    added,
                    removed
                }
            })
        );
    }

    private async editPoint(point: CreatePointRequest) {
        this.pointInEdit = point;
        await this.editCreatePointModal.updateComplete;
        this.editCreatePointModal.show();
    }

    private async savePoint(name:string, description: string) {
        if (this.pointInEdit) {
            this.pointInEdit.description = description;
            this.pointInEdit.name = name;
            // update points in selectedSearchResults and allSearchResults
            const index = this.selectedSearchResults.findIndex((p) => p.location === this.pointInEdit?.location);
            if (index > -1) {
                this.selectedSearchResults[index] = this.pointInEdit;
            }
            const index2 = this.allSearchResults.findIndex((p) => p.location === this.pointInEdit?.location);
            if (index2 > -1) {
                this.allSearchResults[index2] = this.pointInEdit;
            }

            this.editCreatePointModal.close();
        }
    }

    private updateCategory(e:any) {
        if (this.pointInEdit) {
            this.pointInEdit.topic_category_id.$oid = e.detail;
            this.requestUpdate();
        }
    }

    private openUpgradeDialog() {
        // send event to open upgrade dialog
        this.dispatchEvent(new CustomEvent('on-open-upgrade-dialog'));
    }

    private addSearchResult(result: CreatePointRequest) {
        this.selectedSearchResults.push(result);
        this.updateSearchResults([result]);
        this.requestUpdate();
    }

    private removeSearchResult(result: CreatePointRequest) {
        const index = this.selectedSearchResults.findIndex((r) => r.location === result.location);
        if (index > -1) {
            this.selectedSearchResults = this.selectedSearchResults.filter((_, i) => i !== index);
            this.updateSearchResults([], [result]);
        }
        this.requestUpdate();
    }

    private close() {
        this.resetFilters();
        this.dispatchEvent(new CustomEvent('close-and-reset'));
    }

    private hoverOnResult(result: CreatePointRequest) {
        this.focusedMarker = result;
        this.dispatchEvent(
            new CustomEvent('on-result-hover', {
                detail: {
                    result
                }
            })
        );
    }

    render() {
        return html`
            ${this.loadingToMap ? html`
                <loading-element
                    loadingString="Adding Places to Map"
                ></loading-element>
            ` : html`
                <div class="modalContent">
                    <span class="exit-button" @click="${this.close}">
                        <mwc-icon>close</mwc-icon>
                    </span>
                    ${this.allSearchResults.length === 0 && !this.noResult ? html`
                        <h2 class="title">Search and Add Places</h2>
                        <p class="info-paragraph">
                            Search by place type or keyword. Explore broad categories like grocery stores or coffee shops, or specify brands such as Trader Joe's and Starbucks. Proxi generates a list of places for you to add to your map.
                        </p>
                        <div class="modalBody">
                            ${this.loadingNearbyPlaces ? html`
                                <loading-element
                                    loadingString="Searching for Places"
                                ></loading-element>
                            ` : html`
                                ${topicData.topic?.hide_keyword_filters !== true ? html`
                                    <div>
                                        ${this.searchTypes === undefined || this.searchTypes?.length <= 1 ? html`
                                            <proxi-textfield
                                                outlined
                                                id="search"
                                                icon="search"
                                                class="searchInput"
                                                label="Place Type Search"
                                                placeholder="e.g. Italian Restaurants, Coffee Shops"
                                                @input="${(e: any) => this.searchForTypes(e.target.value)}">
                                            </proxi-textfield>
                                        ` : ''}
                                        <div class="suggested-keywords">
                                            <div class="keywords-container flexContainer">
                                                ${this.autoCompleteResults.map((c) => {
                                                        const selected = this.searchTypes?.includes(c) ?? false;
                                                        return html`
                                                            <div
                                                                class="tag__item"
                                                                @click="${() => this.updateType(c)}"
                                                                style="${selected ? `background-color: var(--main-primary-color); color: white` : `background-color: #fff; border: 1px solid #D0D5DD;`}"
                                                                >
                                                                ${c[0]}
                                                        </div>
                                                        `;
                                                    })
                                                }
                                                ${this.searchElement?.value.length > 0 || isDefined(this.searchKeyword)
                                                ? html`
                                                    <div
                                                        class="tag__item"
                                                        @click="${!isDefined(this.searchKeyword) ? () => this.addKeyword(this.searchElement?.value) : () => this.removeKeyword(this.searchElement?.value)}"
                                                        style="${isDefined(this.searchKeyword) ? `background-color: var(--main-primary-color); color: white` : `background-color: #fff; border: 1px solid #D0D5DD;`}"
                                                        >
                                                        Custom Keyword:  <i><b> '${this.searchKeyword ?? this.searchElement?.value}' </b></i>
                                                    </div>
                                                `: ''}
                                            </div>
                                        </div>
                                    </div>
                                ` : ''}
                                <p class="info-paragraph">
                                    <mwc-icon style="margin-right: 5px;">location_searching</mwc-icon>
                                    Set the radius and focus of your search by adjusting the circle on the map.
                                </p>
                            `}
                        </div>
                        ` : ''}
                        <div class="modalBody">
                            ${this.allSearchResults.length > 0 || this.noResult ? html`
                                <div class="search-results">
                                    <h4 class="subtitle">Search Results</h4>
                                    <p class="description">Select the places you'd like to add to your map. Click on a place to edit the category or details.</p>
                                    <div class="results-container">
                                        ${this.allSearchResults.length > 0 ? html`
                                            ${this.allSearchResults.map((result) => {
                                                return html`
                                                    <div class="search-result ${this.focusedMarker === result ? 'active': '' }" @mouseover="${() => this.hoverOnResult(result)}">
                                                        <span style="flex:1; cursor:text" @click="${async () => await this.editPoint(result)}">
                                                            <p class="point-name">
                                                                <span
                                                                    style="display: flex; align-items: center; margin-bottom: 5px; margin-top: 5px;">
                                                                    <img style="height: 16px; width: 16px; margin-right:5px" src="${ifDefined(topicData.categories.find(c => c._id?.$oid === result.topic_category_id.$oid)?.icon)}" />
                                                                    ${result.name}
                                                                </span>
                                                            </p>
                                                            ${result.description && result.description.length > 0 ? html`
                                                                <p class="point-description">${result.description}</p>
                                                            ` : ''}
                                                        </span>
                                                        <mwc-checkbox @click="${this.selectedSearchResults.includes(result) ? () => this.removeSearchResult(result) : () => this.addSearchResult(result)}"
                                                            ?checked="${this.selectedSearchResults.includes(result)}">
                                                        </mwc-checkbox>
                                                    </div>
                                                `;
                                            })}
                                        ` : html `
                                            <p class="description">No results found, please try a different search or try zooming in to a smaller area</p>
                                        `}
                                    </div>
                                </div>
                            ` : ''}
                        </div>
                    <div class="action-buttons">
                        ${this.allSearchResults.length > 0 || this.noResult ? html`
                            <form-button class="clear-btn" variant="outline" @click="${this.resetFilters}">
                                Restart Search
                            </form-button>
                            ${this.selectedSearchResults.length > 0 ? html`
                                <form-button class="search-btn" variant="custom" @click="${async () => await this.addToMap()}">
                                    Add ${this.selectedSearchResults.length} Places to Map
                                </form-button>
                            ` : ''}
                        ` : html`
                            ${(accountInfo.ai_tool_uses_this_month ?? 0) >= featuresInfo.numberOfUsesOfProxiAIEnabled ? html`
                                <form-button class="search-btn" variant="custom" @click="${this.openUpgradeDialog}">
                                    Upgrade for More Searches
                                </form-button>
                            ` : html`
                                ${(isDefined(this.searchKeyword) || (this.searchTypes && this.searchTypes?.length > 0)) && !this.loadingNearbyPlaces ? html`
                                    <form-button class="search-btn" variant="custom" @click="${async() => await this.getNearbyPlaces()}">
                                        Search
                                    </form-button>
                                ` : html`
                                    <form-button class="search-btn" variant="custom" disabled>
                                        Search
                                    </form-button>
                                `}
                            `}
                        `}
                    </div>
                </div>
                <error-toast id="errorToast"></error-toast>
            `}
            <mwc-dialog id="editCreatePoint" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.editCreatePointModal.close()}" >
                    <div slot="title" class="modalTitle">
                        <h3 class="title">Edit Point ${this.pointInEdit?.name ?? ''}</h3>
                    </div>
                    <div slot="body" class="modalBody">
                        <p>Category</p>
                        <category-selector
                            id="categorySelector"
                            canAddCategory
                            .previousValue=${this.pointInEdit?.topic_category_id?.$oid}
                            @select=${this.updateCategory}>
                        </category-selector>
                        <form-input
                            id="pointName"
                            label="Name"
                            placeholder="Name"
                            value="${this.pointInEdit?.name ?? ''}">
                        </form-input>
                        <form-textarea
                            id="pointDescription"
                            label="Description"
                            placeholder="Description"
                            value="${this.pointInEdit?.description ?? ''}">
                        </form-textarea>
                        <div class="modalFooter">
                            <form-button
                                class="save-btn"
                                variant="custom"
                                @click="${async () => await this.savePoint(this.pointNameElement?.value ?? this.pointInEdit?.name ?? '', this.pointDescriptionElement?.value ?? this.pointInEdit?.description ?? '')}">
                                Save
                            </form-button>
                        </div>
                    </div>

                </modal-dialog>
            </mwc-dialog>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
            }

            h4 {
                margin: 5px 0;
            }

            .exit-button {
                position: absolute;
                top: 1rem;
                right: 0;
                padding: 1rem;
                cursor: pointer;
                --mdc-icon-size: 32px;
            }

            .title {
                color: #000;
                text-align: left;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 0;
            }

            .subtitle {
                color: #091E2C;
                font-size: 16px;
                line-height: 150%;
                margin: 0;
            }

            .description {
                color: #091E2C;
                font-size: 16px;
                line-height: 150%;
                margin: 0;
            }

            .tag__item {
                position: relative;
                user-select: none;
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 3px 8px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid var(--main-primary-color);
                --mwc-icon-size: 12px;
            }

            .point-name {
                display: flex;
                gap: 5px;
                align-items: center;
                justify-content: space-between;
                margin: 0;
                margin-top: 5px;
            }

            .modalFooter {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                color: white;
                padding: 5px;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
                font-size: 0.8rem;
                line-height: 120%;
                margin-top: 0.5rem;
                display: inline-flex;
                align-items: center;
            }

            .point-description {
                margin: 0;
                font-weight: normal;
                margin-bottom: 5px;
            }

            .modalContent {
                width: 360px;
                --mdc-icon-size: 18px;
                display: flex;
                flex-direction: column;
                min-height: 40vh;
                max-height: 60vh;
                text-align: left;
            }

            .modalTitle {
                text-align: center;
                margin-bottom: 1.5rem;
                margin-top: 0;
            }

            .modalBody {
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
            }

            .modalBody::-webkit-scrollbar {
                width: 5px;
            }

            .modalBody::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .modalBody::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .search-result {
                display: flex;
                flex-direction: row;
                align-content: center;
                justify-content: space-between;
                align-items: center;
                border-bottom: grey 1px solid;
                cursor: pointer;
                padding-right: 1rem;
            }

            .search-result:first-child {
                border-top: grey 1px solid;
            }

            .search-result.active {
                background-color: var(--theme-border-color);
                font-weight: bold;
            }

            .flexContainer {
                display: flex;
                align-items: center;
                margin-right: 20px;
            }

            .keywords-container {
                margin: 0.5rem 0;
                flex-wrap: wrap;
            }

            .top-level-tags-container {
                width: 100%;
            }

            .action-buttons {
                display: flex;
                justify-content: flex-end;
                gap: 1rem;
                margin-top: 0.5rem;
            }

            .search-btn {
                --btn-background-color: var(--main-success-color);
                --btn-label-color: white;
            }

            .tags-container {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                border-left: 1px solid var(--main-primary-color);
            }

            .searchInput {
                margin-top: 1rem;
                width: 100%;
            }

            @media (max-width: 600px) {
                .modalContent {
                    width: 100%;
                }

                .modalBody {
                    padding: 0;
                    overflow-x: hidden;
                }

                .top-level-tags-container{
                    width: 90%;
                }

                .tags-container {
                    margin-left: 10px;
                }
            }
        `;
    }
}
